import { useState, useEffect } from "react";
import { NavigationArrow, Star, MapPin, Spinner, Target, ShieldCheck, ShieldWarning, XCircle, Timer } from "@phosphor-icons/react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import API from "../lib/api";
import SlaShield from "./SlaShield";

/**
 * Panel showing suppliers sorted by distance from a given origin (project/task/wo lat-lng).
 *
 * Props:
 *   projectId     - optional, fetches origin + inherits trade from project.category
 *   taskId        - optional, inherits trade from task.category
 *   woId          - optional
 *   latitude/longitude - explicit origin (overrides the IDs)
 *   trade         - optional trade filter (overrides inherited)
 *   radiusMiles   - default 50
 *   limit         - default 8
 *   onPick(vendor) - called when the user clicks a supplier
 *   compact       - bool: denser layout
 *   title         - panel header
 *   autoLoad      - bool (default true)
 *   defaultSort   - "distance" | "response" | "score" (default "distance")
 */

/**
 * Score = normalised distance + normalised response time, where
 *   distance_norm = distance_miles / 50  (cap at 50 mi → 1.0)
 *   response_norm = response_seconds / 7200  (cap at 2 hours → 1.0)
 * Lower score wins. Suppliers missing response data are penalised (treated as 2h baseline
 * so they still rank, but below anyone with a known-fast response time).
 */
function _computeSlaScore(s) {
  const distKm = Math.max(0, s.distance_miles ?? 99);
  const distNorm = Math.min(distKm / 50, 1);
  const respSec = s.avg_response_seconds;
  // Missing data → neutral 2h (treat as "unknown, might be slow")
  const respNorm = Math.min((respSec ?? 7200) / 7200, 1);
  // Slight preference for response time (0.45 vs 0.55) since nearest isn't always best
  return distNorm * 0.45 + respNorm * 0.55;
}

function _slaBadge(s) {
  // Grade visual hint based on SLA score; helpful to scan down the list
  const score = _computeSlaScore(s);
  if (score < 0.25) return { label: "Excellent", classes: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" };
  if (score < 0.45) return { label: "Good", classes: "bg-sky-500/15 text-sky-700 dark:text-sky-400" };
  if (score < 0.7)  return { label: "Fair", classes: "bg-amber-500/15 text-amber-700 dark:text-amber-400" };
  return { label: "Slow", classes: "bg-rose-500/15 text-rose-700 dark:text-rose-400" };
}

export default function NearbySuppliers({
  projectId = "",
  taskId = "",
  woId = "",
  jobId = "",
  emergencyJobId = "",
  jrId = "",
  latitude = null,
  longitude = null,
  trade = "",
  radiusMiles = 50,
  limit = 8,
  onPick,
  compact = false,
  title = "Nearby Suppliers",
  autoLoad = true,
  showTradeInput = false,
  compliantOnly: initialCompliantOnly = false,
  includeCompliance = false,
  showAutoPick = false,
  defaultSort = "distance", // "distance" | "response" | "score"
  "data-testid": testId = "nearby-suppliers",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tradeOverride, setTradeOverride] = useState(trade);
  const [radius, setRadius] = useState(radiusMiles);
  const [compliantOnly, setCompliantOnly] = useState(initialCompliantOnly);
  const [sortBy, setSortBy] = useState(defaultSort); // distance | response | score

  const fetchNearby = async () => {
    const hasAnySource = projectId || taskId || woId || jobId || emergencyJobId || jrId || (latitude && longitude);
    if (!hasAnySource) {
      setData(null);
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const params = { radius_miles: radius, limit };
      if (projectId) params.project_id = projectId;
      if (taskId) params.task_id = taskId;
      if (woId) params.wo_id = woId;
      if (jobId) params.job_id = jobId;
      if (emergencyJobId) params.emergency_job_id = emergencyJobId;
      if (jrId) params.jr_id = jrId;
      if (latitude && longitude) {
        params.latitude = latitude;
        params.longitude = longitude;
      }
      if (tradeOverride) params.trade = tradeOverride;
      if (compliantOnly) params.compliant_only = true;
      if (includeCompliance || compliantOnly) params.include_compliance = true;
      const { data: resp } = await API.get("/suppliers-nearby", { params });
      setData(resp);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load nearby suppliers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad) fetchNearby();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, taskId, woId, jobId, emergencyJobId, jrId, latitude, longitude, tradeOverride, radius, compliantOnly]);

  // Auto-pick: on demand, find the top compliant match (ignoring current compliantOnly state)
  // When sortBy === "score" we fetch a larger pool and apply SLA scoring client-side
  // to find the best overall pick (distance + response time combined).
  const handleAutoPick = async () => {
    if (!onPick) return;
    setLoading(true);
    try {
      const params = {
        radius_miles: radius,
        limit: sortBy === "score" ? Math.max(limit, 15) : 1,
        compliant_only: true, include_compliance: true,
      };
      if (projectId) params.project_id = projectId;
      if (taskId) params.task_id = taskId;
      if (woId) params.wo_id = woId;
      if (jobId) params.job_id = jobId;
      if (emergencyJobId) params.emergency_job_id = emergencyJobId;
      if (jrId) params.jr_id = jrId;
      if (latitude && longitude) { params.latitude = latitude; params.longitude = longitude; }
      if (tradeOverride) params.trade = tradeOverride;
      const { data: resp } = await API.get("/suppliers-nearby", { params });
      const candidates = resp?.suggestions || [];
      if (candidates.length > 0) {
        const best = sortBy === "score"
          ? [...candidates].sort((a, b) => _computeSlaScore(a) - _computeSlaScore(b))[0]
          : candidates[0];
        onPick(best);
      } else {
        setErr("No compliant suppliers found within " + radius + " miles. Widen the radius or add compliance documents.");
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Auto-pick failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`border border-border rounded-md bg-card overflow-hidden ${compact ? "" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MapPin size={14} weight="duotone" className="text-primary" />
          <h3 className="text-sm font-semibold font-[Chivo]">{title}</h3>
          {data?.total_matches > 0 && (
            <Badge variant="outline" className="text-[9px] py-0 h-4">{data.total_matches}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {showAutoPick && onPick && (
            <Button
              size="sm"
              onClick={handleAutoPick}
              disabled={loading}
              className="h-6 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`${testId}-auto-pick`}
              title={sortBy === "score"
                ? "Pick the best compliant supplier by SLA score (distance + response time)"
                : "Pick the closest compliant supplier matching this trade"}
            >
              <Target size={11} className="mr-1" weight="fill" />
              {sortBy === "score" ? "Auto-pick best SLA" : "Auto-pick closest compliant"}
            </Button>
          )}
          {(showAutoPick || includeCompliance) && (
            <label className="flex items-center gap-1 text-[10px] cursor-pointer select-none" data-testid={`${testId}-compliant-toggle`}>
              <input
                type="checkbox"
                checked={compliantOnly}
                onChange={e => setCompliantOnly(e.target.checked)}
                className="h-3 w-3 accent-emerald-600"
              />
              Compliant only
            </label>
          )}
          {showTradeInput && (
            <input
              type="text"
              value={tradeOverride}
              onChange={e => setTradeOverride(e.target.value)}
              onBlur={fetchNearby}
              placeholder="trade..."
              className="h-6 text-[11px] px-2 rounded border border-border bg-background w-24"
              data-testid={`${testId}-trade-input`}
            />
          )}
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="h-6 text-[11px] px-2 rounded border border-border bg-background"
            title="Sort results"
            data-testid={`${testId}-sort-select`}
          >
            <option value="distance">Nearest</option>
            <option value="response">Fastest response</option>
            <option value="score">Best overall (distance + SLA)</option>
          </select>
          <select
            value={radius}
            onChange={e => setRadius(Number(e.target.value))}
            className="h-6 text-[11px] px-2 rounded border border-border bg-background"
            data-testid={`${testId}-radius-select`}
          >
            <option value={10}>10 mi</option>
            <option value={25}>25 mi</option>
            <option value={50}>50 mi</option>
            <option value={100}>100 mi</option>
            <option value={250}>250 mi</option>
          </select>
        </div>
      </div>

      {data?.origin?.trade && (
        <div className="px-3 py-1.5 bg-blue-50 dark:bg-blue-950/30 border-b border-border text-[10px] text-blue-700 dark:text-blue-300" data-testid={`${testId}-trade-chip`}>
          Filtered by trade: <strong className="capitalize">{data.origin.trade}</strong>
        </div>
      )}

      {loading && (
        <div className="py-6 flex items-center justify-center text-muted-foreground text-xs">
          <Spinner size={14} className="animate-spin mr-2" /> Finding nearest suppliers...
        </div>
      )}

      {!loading && err && (
        <div className="py-4 px-3 text-xs text-red-600 dark:text-red-400" data-testid={`${testId}-error`}>{err}</div>
      )}

      {!loading && data?.warning && (
        <div className="py-4 px-3 text-xs text-amber-600 dark:text-amber-400" data-testid={`${testId}-warning`}>{data.warning}</div>
      )}

      {!loading && !err && data && data.suggestions?.length === 0 && !data.warning && (
        <div className="py-6 text-center text-muted-foreground text-xs">
          No suppliers within {data.radius_miles} miles{data.origin.trade ? ` matching "${data.origin.trade}"` : ""}.
        </div>
      )}

      {!loading && data?.suggestions?.length > 0 && (
        <div className="divide-y divide-border">
          {[...data.suggestions].sort((a, b) => {
            if (sortBy === "score") {
              const sa = _computeSlaScore(a);
              const sb = _computeSlaScore(b);
              if (sa !== sb) return sa - sb;
              return a.distance_miles - b.distance_miles;
            }
            if (sortBy === "response") {
              const ax = a.avg_response_seconds ?? Number.MAX_SAFE_INTEGER;
              const bx = b.avg_response_seconds ?? Number.MAX_SAFE_INTEGER;
              if (ax !== bx) return ax - bx;
              return a.distance_miles - b.distance_miles;
            }
            return a.distance_miles - b.distance_miles;
          }).map(s => {
            const badge = sortBy === "score" ? _slaBadge(s) : null;
            return (
            <div
              key={s.id}
              role={onPick ? "button" : undefined}
              onClick={onPick ? () => onPick(s) : undefined}
              className={`flex items-center justify-between px-3 py-2 ${onPick ? "hover:bg-muted/50 cursor-pointer" : ""} transition-colors`}
              data-testid={`${testId}-row-${s.id}`}
            >
              <div className="flex items-start gap-2 min-w-0 flex-1">
                <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-[10px] font-mono font-bold text-primary">{s.distance_miles}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium truncate" data-testid={`${testId}-name-${s.id}`}>{s.company_name}</p>
                    {s.trade && <Badge variant="outline" className="text-[9px] py-0 h-4 capitalize">{s.trade}</Badge>}
                    {badge && (
                      <span
                        className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${badge.classes}`}
                        title={`SLA score combines distance + recent response time. Lower = better.`}
                        data-testid={`${testId}-sla-badge-${s.id}`}
                      >
                        {badge.label}
                      </span>
                    )}
                    {!badge && (s.sla_grade || s.sla_response_count != null) && (
                      <SlaShield vendor={s} showLabel className="!py-0" />
                    )}
                    {s.compliance_status === "compliant" && (
                      <span className="flex items-center gap-0.5 text-[10px] text-emerald-600 dark:text-emerald-400" title="Compliant">
                        <ShieldCheck size={10} weight="fill" /> Compliant
                      </span>
                    )}
                    {s.compliance_status === "warning" && (
                      <span className="flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400" title="Documents expiring soon">
                        <ShieldWarning size={10} weight="fill" /> Warning
                      </span>
                    )}
                    {s.compliance_status === "non_compliant" && (
                      <span className="flex items-center gap-0.5 text-[10px] text-red-600 dark:text-red-400" title="Missing or expired documents">
                        <XCircle size={10} weight="fill" /> Non-compliant
                      </span>
                    )}
                    {s.average_rating > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-amber-600">
                        <Star size={10} weight="fill" /> {s.average_rating.toFixed(1)}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {[s.contact_name, s.postcode, s.town_city].filter(Boolean).join(" · ")}
                  </p>
                  {s.avg_response_label && (
                    <p className="text-[10px] text-primary flex items-center gap-1 mt-0.5" title={`Avg response time across ${s.response_count} recent tasks`}>
                      <Timer size={10} weight="fill" /> responds in ~{s.avg_response_label}
                      <span className="text-muted-foreground">({s.response_count})</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-2">
                {s.latitude && s.longitude && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${s.latitude},${s.longitude}&travelmode=driving`, "_blank", "noopener,noreferrer");
                    }}
                    title="Open directions to this supplier"
                    data-testid={`${testId}-directions-${s.id}`}
                  >
                    <NavigationArrow size={12} weight="fill" />
                  </Button>
                )}
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}
